import { useState } from 'react'
import { Button, Flex, NumberInput, Slider, Switch, Text, Tooltip, rem } from '@mantine/core'
import ImageObserved from '../pipeline/Image'
import { PipelineParameters } from '../pipeline/Pipeline'
import { NOTIFICATION_SERVICE } from '../../../components/notifications/NotificationService'
import { useStore } from '../../../hooks/use-store'
import { observer } from 'mobx-react'
import { priceMap } from '../pipeline/Router'
import { useHistory } from 'react-router-dom'

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

function CameraRotate(model: {
  buy: (params: PipelineParameters[]) => Promise<void>;
  loading: boolean
}) {

  const {
    dataStore: { spaceStore },
  } = useStore();

  const history = useHistory()

  const [rotateRightLeft, setRotateRightLeft] = useState(0)
  const [moveForward, setMoveForward] = useState(0)
  const [verticalAngle, setVerticalAngle] = useState(0)
  const [wideAngleLens, setWideAngleLens] = useState(false)

  const computedCredits = priceMap.CAMERA_ROTATE ?? priceMap.REMOVE_OBJECT ?? 10

  const canRun = Boolean(spaceStore.selectedImage)

  const handleSubmit = async () => {
    if (!spaceStore.selectedImage) {
      NOTIFICATION_SERVICE.errorNotification('Please select a base image first.')
      return
    }

    const pipeline: PipelineParameters = {
      provider: 'custom',
      type: 'cameraRotateV1',
      rotate_right_left: rotateRightLeft,
      move_forward: moveForward,
      vertical_angle: verticalAngle,
      wide_angle_lens: wideAngleLens,
      inputSrc: spaceStore.selectedImage,
      maskSrc: '',
      srcset: [],
      credits: computedCredits,
    }

    await model.buy([pipeline]);
  }

  const parameterControls = [
    {
      key: 'rotate_right_left',
      label: 'Rotate left / right',
      description: 'Positive values rotate the camera left, negative values rotate right.',
      value: rotateRightLeft,
      setter: setRotateRightLeft,
      min: -90,
      max: 90,
      step: 1,
      precision: 0,
      unit: '°',
    },
    {
      key: 'move_forward',
      label: 'Move forward',
      description: 'Higher values move the camera toward the subject for a close-up feel.',
      value: moveForward,
      setter: setMoveForward,
      min: 0,
      max: 10,
      step: 0.1,
      precision: 1,
      unit: '',
    },
    {
      key: 'vertical_angle',
      label: 'Vertical angle',
      description: '-1 tilts down for a bird’s-eye view, 1 tilts up for a worm’s-eye view.',
      value: verticalAngle,
      setter: setVerticalAngle,
      min: -1,
      max: 1,
      step: 0.1,
      precision: 1,
      unit: '',
    },
  ] as const

  return (
    <Flex w={'100%'} mt={12} pt={32} pb={32} pl={40} pr={40} style={{ borderRadius: '4px', background: 'rgba(32, 32, 32, 1)' }} gap={32} pos='relative'>
      <Flex pos='absolute' top='0' right='0' style={{ cursor: 'pointer' }} onClick={() => {
        history.push('/history')
      }}>
        <svg width="40" height="40" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.17157 11.2944L10.8284 5.63758" stroke="#BDBDBD" stroke-linecap="round" stroke-linejoin="round"></path><path d="M10.8284 11.2945L5.17157 5.6377" stroke="#BDBDBD" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </Flex>
      <Flex direction={'column'} w={'40%'} gap={20}>

        <Flex direction="column" gap={8}>
          <Text fz={32} fw={800} color='#fff' lh={1.2}>Camera Rotate.</Text>
          <Text fz={16} color='#fff' lh={1.4} maw={440}>
            Re-compose your shot without regenerating a scene. Adjust horizontal rotation, dolly forward, tilt the camera, and optionally enable a wide-angle lens effect.
            <Tooltip
              label={
                <Text fw={500}>
                  Rotate the existing camera to explore new angles from the same generation.
                  Perfect for rapid look-dev when you already like the environment.
                </Text>
              }
              multiline
              w={320}
              withArrow
              position="bottom"
              color='dark'
            >
              <span style={{ marginLeft: 4, cursor: 'pointer', display: 'inline-block' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="#4badf4" className="icon icon-tabler icons-tabler-filled icon-tabler-info-square-rounded"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm0 9h-1l-.117 .007a1 1 0 0 0 0 1.986l.117 .007v3l.007 .117a1 1 0 0 0 .876 .876l.117 .007h1l.117 -.007a1 1 0 0 0 .876 -.876l.007 -.117l-.007 -.117a1 1 0 0 0 -.764 -.857l-.112 -.02l-.117 -.006v-3l-.007 -.117a1 1 0 0 0 -.876 -.876l-.117 -.007zm.01 -3l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007z" /></svg>
              </span>
            </Tooltip>
          </Text>
        </Flex>

        <Flex direction="column" gap={16}>
          {parameterControls.map((control) => (
            <Flex
              key={control.key}
              direction="column"
              gap={8}
              p={16}
              style={{ backgroundColor: 'rgba(24,24,24,1)', borderRadius: '8px', border: '1px solid rgba(70, 70, 70, 1)' }}
            >
              <Flex justify="space-between" align="center" gap={12}>
                <Flex direction="column">
                  <Text color="#fff" size="sm" fw={600}>{control.label}</Text>
                  <Text color="#BDBDBD" size="xs">{control.description}</Text>
                </Flex>
                <NumberInput
                  value={control.value}
                  min={control.min}
                  max={control.max}
                  step={control.step}
                  precision={control.precision}
                  onChange={(val) => control.setter(typeof val === 'number' ? clamp(val, control.min, control.max) : control.min)}
                  styles={{
                    input: {
                      backgroundColor: 'rgba(0, 0, 0, 0.4)',
                      color: '#fff',
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                      minWidth: '96px'
                    }
                  }}
                  size="xs"
                />
              </Flex>
              <Slider
                min={control.min}
                max={control.max}
                step={control.step}
                value={control.value}
                onChange={control.setter}
                label={(value) => `${value.toFixed(2)}${control.unit}`}
                styles={{
                  track: { backgroundColor: 'rgba(255,255,255,0.1)' },
                  bar: { backgroundColor: '#f53c02' },
                  thumb: { borderColor: '#f53c02', backgroundColor: '#f53c02' },
                }}
              />
            </Flex>
          ))}

          <Flex
            direction="column"
            gap={8}
            p={16}
            style={{ backgroundColor: 'rgba(24,24,24,1)', borderRadius: '8px', border: '1px solid rgba(70, 70, 70, 1)' }}
          >
            <Flex justify="space-between" align="center">
              <Flex direction="column">
                <Text color="#fff" size="sm" fw={600}>Wide-angle lens</Text>
                <Text color="#BDBDBD" size="xs">Enable to exaggerate perspective and capture more of the scene.</Text>
              </Flex>
              <Switch
                checked={wideAngleLens}
                onChange={(event) => setWideAngleLens(event.currentTarget.checked)}
                color="orange"
                size="md"
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex direction="column" gap={8}>
          <Text size="sm" color="#BDBDBD">
            Generation cost:&nbsp;
            <Text component="span" color="#f53c02" fw={700}>{computedCredits} credits</Text>
          </Text>
          <Button
            styles={{
              root: {
                color: 'rgba(230, 230, 230, 1)',
                height: rem(40),
                fontSize: '14px',
                background: '#f53c02 !important',
                border: 'none',
                position: 'relative',
              },
            }}
            variant="default"
            disabled={!canRun}
            loading={model.loading}
            onClick={handleSubmit}
          >
            Rotate Camera
          </Button>
          {!canRun && (
            <Text size="xs" color="#BDBDBD">
              Select an existing generation to enable camera rotation.
            </Text>
          )}
        </Flex>
      </Flex>
      <Flex justify={'center'} direction={'column'} align={'center'} w={'60%'} style={{ backgroundColor: 'rgba(18, 18, 18, 1)', borderRadius: '8px' }} p={16}>
        {
          spaceStore.selectedImage ? (
            <ImageObserved
              width={'100%'}
              height={'100%'}
              dark
              src={spaceStore.selectedImage}
            />
          ) : (
            <Flex align="center" justify="center" w="100%" h="100%" style={{ border: '1px dashed rgba(189, 189, 189, 0.4)', borderRadius: '8px' }} p={24}>
              <Text color="#BDBDBD" align="center">
                Pick or upload an image to preview new camera angles.
              </Text>
            </Flex>
          )
        }
      </Flex>
    </Flex>
  )
}

export default observer(CameraRotate);